import React, { useState, useEffect } from "react";
import { useApi } from "../../hooks/useApi";
import { useTheme } from "../../contexts/ThemeContext";
import { AdminDesignSystem, ADMIN_ROOT } from "./adminDesignSystem";
import AdminSidebar from "./AdminSidebar";
import toast from "react-hot-toast";
import {
  InputNumber,
  Select,
  DatePicker,
  ConfigProvider,
  theme as antdTheme,
} from "antd";
import dayjs from "dayjs";
import {
  Tag,
  Plus,
  CheckCircle,
  XCircle,
  Calendar,
  Percent,
  Copy,
  Users,
  Sparkles,
  X,
  TrendingUp,
  Clock,
} from "lucide-react";

const CouponManagement = () => {
  const api = useApi();
  const { mode } = useTheme();
  const isDark = mode === "dark";

  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [formData, setFormData] = useState({
    id: "",
    name: "",
    discountType: "percent", // 'percent' or 'amount'
    percent_off: "",
    amount_off: "",
    currency: "usd",
    duration: "once", // 'once', 'repeating', 'forever'
    duration_in_months: "",
    max_redemptions: "",
    redeem_by: "",
    min_member_count: "",
    max_member_count: "",
  });

  useEffect(() => {
    fetchCoupons();
  }, []);

  const fetchCoupons = async () => {
    try {
      setLoading(true);
      const response = await api.get("/api/v1/billing/coupons");
      setCoupons(response.data.data.coupons || []);
    } catch (error) {
      toast.error("Failed to fetch coupons");
      console.error("Error fetching coupons:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCoupon = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        name: formData.name,
        duration: formData.duration,
      };

      if (formData.id) payload.id = formData.id;

      if (formData.discountType === "percent") {
        // Allow floating point percentages (e.g., 34.56%)
        payload.percent_off = parseFloat(formData.percent_off);
        if (
          isNaN(payload.percent_off) ||
          payload.percent_off <= 0 ||
          payload.percent_off > 100
        ) {
          toast.error("Percentage must be between 0.01 and 100");
          return;
        }
      } else {
        // Allow floating point amounts (e.g., 34.56)
        payload.amount_off = Math.round(parseFloat(formData.amount_off) * 100); // Convert to cents
        payload.currency = formData.currency.toLowerCase();
        if (isNaN(payload.amount_off) || payload.amount_off <= 0) {
          toast.error("Amount must be greater than 0");
          return;
        }
      }

      if (formData.duration === "repeating") {
        payload.duration_in_months = parseInt(formData.duration_in_months);
      }

      if (formData.max_redemptions) {
        payload.max_redemptions = parseInt(formData.max_redemptions);
      }

      if (formData.redeem_by) {
        payload.redeem_by = formData.redeem_by;
      }

      if (formData.min_member_count) {
        payload.min_member_count = parseInt(formData.min_member_count);
      }

      if (formData.max_member_count) {
        payload.max_member_count = parseInt(formData.max_member_count);
      }

      await api.post("/api/v1/billing/create-coupon", payload);
      toast.success("Coupon created successfully!");
      setShowCreateModal(false);
      resetForm();
      fetchCoupons();
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to create coupon");
      console.error("Error creating coupon:", error);
    }
  };

  const resetForm = () => {
    setFormData({
      id: "",
      name: "",
      discountType: "percent",
      percent_off: "",
      amount_off: "",
      currency: "usd",
      duration: "once",
      duration_in_months: "",
      max_redemptions: "",
      redeem_by: "",
      min_member_count: "",
      max_member_count: "",
    });
  };

  const copyCouponId = (couponId) => {
    navigator.clipboard.writeText(couponId);
    toast.success("Coupon code copied to clipboard!");
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return "Never";
    return new Date(timestamp * 1000).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const getDiscountText = (coupon) => {
    if (coupon.percent_off) {
      // Show percentage with decimals if needed
      const percent =
        coupon.percent_off % 1 === 0
          ? coupon.percent_off.toString()
          : coupon.percent_off.toFixed(2);
      return `${percent}% OFF`;
    } else if (coupon.amount_off) {
      const amount = (coupon.amount_off / 100).toFixed(2);
      return `$${amount} OFF`;
    }
    return "Discount";
  };

  const getDurationBadge = (duration) => {
    const badges = {
      once: {
        text: "One-time",
        color: isDark
          ? "bg-blue-500/20 text-blue-400"
          : "bg-blue-100 text-blue-700",
      },
      repeating: {
        text: "Repeating",
        color: isDark
          ? "bg-purple-500/20 text-purple-400"
          : "bg-purple-100 text-purple-700",
      },
      forever: {
        text: "Forever",
        color: isDark
          ? "bg-green-500/20 text-green-400"
          : "bg-green-100 text-green-700",
      },
    };
    return badges[duration] || badges.once;
  };

  return (
    <div className={`${ADMIN_ROOT} h-screen flex overflow-hidden bg-black`}>
      <AdminDesignSystem />
      <AdminSidebar activeKey="coupons" />

      {/* Main content */}
      <div className="flex flex-col w-0 flex-1 overflow-hidden">
        <main className="flex-1 relative z-0 overflow-y-auto focus:outline-none">
          <div className="py-6">
            <div className="w-full px-5 sm:px-6">
              {/* Header */}
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8 gap-4">
                <div>
                  <div className="flex items-center gap-3 mb-2">
                    <div
                      className={`p-3 rounded-xl ${isDark ? "bg-blue-500/20" : "bg-blue-100"}`}
                    >
                      <Tag
                        className={`h-6 w-6 ${isDark ? "text-blue-400" : "text-blue-600"}`}
                      />
                    </div>
                    <div>
                      <h1
                        className={`text-3xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}
                      >
                        Coupon Management
                      </h1>
                      <p
                        className={`text-sm mt-1 ${isDark ? "text-gray-400" : "text-gray-600"}`}
                      >
                        {coupons.length}{" "}
                        {coupons.length === 1 ? "coupon" : "coupons"} available
                      </p>
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => setShowCreateModal(true)}
                  className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 !text-white rounded-xl font-medium shadow-lg hover:shadow-xl transition-all duration-200 transform hover:scale-105"
                >
                  <Plus className="h-5 w-5" />
                  Create Coupon
                </button>
              </div>

              {/* Coupons List */}
              {loading ? (
                <div className="flex flex-col items-center justify-center py-20">
                  <div
                    className={`animate-spin rounded-full h-12 w-12 border-b-2 ${isDark ? "border-blue-400" : "border-blue-600"}`}
                  ></div>
                  <p
                    className={`mt-4 ${isDark ? "text-gray-400" : "text-gray-600"}`}
                  >
                    Loading coupons...
                  </p>
                </div>
              ) : coupons.length === 0 ? (
                <div
                  className={`text-center py-20 rounded-2xl border-2 border-dashed ${isDark ? "border-gray-700 bg-gray-800/50" : "border-gray-300 bg-gray-50"}`}
                >
                  <div
                    className={`inline-flex p-4 rounded-full mb-4 ${isDark ? "bg-gray-800" : "bg-gray-100"}`}
                  >
                    <Tag
                      className={`h-8 w-8 ${isDark ? "text-gray-500" : "text-gray-400"}`}
                    />
                  </div>
                  <h3
                    className={`text-xl font-semibold mb-2 ${isDark ? "text-white" : "text-gray-900"}`}
                  >
                    No coupons found
                  </h3>
                  <p
                    className={`mb-6 ${isDark ? "text-gray-400" : "text-gray-600"}`}
                  >
                    Create your first coupon to start offering discounts
                  </p>
                  <button
                    onClick={() => setShowCreateModal(true)}
                    className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
                  >
                    <Plus className="h-5 w-5" />
                    Create Your First Coupon
                  </button>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {coupons.map((coupon) => {
                    const discountText = getDiscountText(coupon);
                    const durationBadge = getDurationBadge(coupon.duration);
                    const isExpired =
                      coupon.redeem_by &&
                      new Date(coupon.redeem_by * 1000) < new Date();

                    return (
                      <div
                        key={coupon.id}
                        className={`group relative overflow-hidden rounded-2xl border transition-all duration-300 hover:shadow-2xl hover:scale-[1.02] ${
                          isDark
                            ? "bg-gradient-to-br from-gray-800 to-gray-800/80 border-gray-700 hover:border-blue-500/50"
                            : "bg-white border-gray-200 hover:border-blue-300 shadow-lg hover:shadow-xl"
                        }`}
                      >
                        {/* Status Badge */}
                        <div className="absolute top-4 right-4 z-10">
                          {coupon.valid && !isExpired ? (
                            <div className="flex items-center gap-1 px-2.5 py-1 rounded-full bg-green-500/20 border border-green-500/30">
                              <CheckCircle className="h-3.5 w-3.5 text-green-400" />
                              <span className="text-xs font-medium text-green-400">
                                Active
                              </span>
                            </div>
                          ) : (
                            <div className="flex items-center gap-1 px-2.5 py-1 rounded-full bg-red-500/20 border border-red-500/30">
                              <XCircle className="h-3.5 w-3.5 text-red-400" />
                              <span className="text-xs font-medium text-red-400">
                                Invalid
                              </span>
                            </div>
                          )}
                        </div>

                        {/* Discount Badge */}
                        <div
                          className={`absolute top-0 left-0 right-0 h-2 bg-gradient-to-r ${
                            coupon.percent_off
                              ? "from-purple-500 to-pink-500"
                              : "from-blue-500 to-cyan-500"
                          }`}
                        ></div>

                        <div className="p-6 pt-8">
                          {/* Header */}
                          <div className="flex items-start justify-between mb-4">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-2">
                                <div
                                  className={`p-2 rounded-lg ${isDark ? "bg-blue-500/20" : "bg-blue-100"}`}
                                >
                                  <Tag
                                    className={`h-4 w-4 ${isDark ? "text-blue-400" : "text-blue-600"}`}
                                  />
                                </div>
                                <h3
                                  className={`font-bold text-lg truncate ${isDark ? "text-white" : "text-gray-900"}`}
                                >
                                  {coupon.name || "Unnamed Coupon"}
                                </h3>
                              </div>
                              <div
                                className={`text-2xl font-bold mb-3 ${
                                  coupon.percent_off
                                    ? isDark
                                      ? "text-purple-400"
                                      : "text-purple-600"
                                    : isDark
                                      ? "text-blue-400"
                                      : "text-blue-600"
                                }`}
                              >
                                {discountText}
                              </div>
                            </div>
                          </div>

                          {/* Promotion Code */}
                          <div
                            className={`mb-4 p-3 rounded-xl ${isDark ? "bg-gray-900/50 border border-gray-700" : "bg-gray-50 border border-gray-200"}`}
                          >
                            <div className="flex items-center justify-between gap-2">
                              <div className="flex-1 min-w-0">
                                <p
                                  className={`text-xs font-medium mb-1 ${isDark ? "text-gray-400" : "text-gray-600"}`}
                                >
                                  Promotion Code
                                </p>
                                <code
                                  className={`text-sm font-mono font-bold block truncate ${isDark ? "text-blue-400" : "text-blue-600"}`}
                                >
                                  {coupon.promotionCode || coupon.id}
                                </code>
                              </div>
                              <button
                                onClick={() =>
                                  copyCouponId(
                                    coupon.promotionCode || coupon.id,
                                  )
                                }
                                className={`flex-shrink-0 p-2 rounded-lg transition-colors ${
                                  isDark
                                    ? "hover:bg-gray-700 text-gray-400 hover:text-blue-400"
                                    : "hover:bg-gray-100 text-gray-500 hover:text-blue-600"
                                }`}
                                title="Copy Code"
                              >
                                <Copy className="h-4 w-4" />
                              </button>
                            </div>
                          </div>

                          {/* Details */}
                          <div
                            className={`space-y-3 text-sm ${isDark ? "text-gray-300" : "text-gray-700"}`}
                          >
                            {/* Duration Badge */}
                            <div className="flex items-center gap-2">
                              <Clock
                                className={`h-4 w-4 ${isDark ? "text-gray-500" : "text-gray-400"}`}
                              />
                              <span className="font-medium">Duration:</span>
                              <span
                                className={`px-2 py-0.5 rounded-md text-xs font-medium ${durationBadge.color}`}
                              >
                                {durationBadge.text}
                              </span>
                            </div>

                            {/* Max Redemptions */}
                            {coupon.max_redemptions && (
                              <div className="flex items-center gap-2">
                                <TrendingUp
                                  className={`h-4 w-4 ${isDark ? "text-gray-500" : "text-gray-400"}`}
                                />
                                <span className="font-medium">Uses:</span>
                                <span
                                  className={
                                    isDark ? "text-gray-400" : "text-gray-600"
                                  }
                                >
                                  {coupon.times_redeemed || 0} /{" "}
                                  {coupon.max_redemptions}
                                </span>
                                <div className="flex-1 h-2 rounded-full overflow-hidden bg-gray-700">
                                  <div
                                    className="h-full bg-blue-500 transition-all"
                                    style={{
                                      width: `${Math.min(((coupon.times_redeemed || 0) / coupon.max_redemptions) * 100, 100)}%`,
                                    }}
                                  ></div>
                                </div>
                              </div>
                            )}

                            {/* Expiry Date */}
                            {coupon.redeem_by && (
                              <div
                                className={`flex items-center gap-2 p-2 rounded-lg ${
                                  isExpired
                                    ? isDark
                                      ? "bg-red-500/10 border border-red-500/20"
                                      : "bg-red-50 border border-red-200"
                                    : isDark
                                      ? "bg-gray-900/50"
                                      : "bg-gray-50"
                                }`}
                              >
                                <Calendar
                                  className={`h-4 w-4 ${isExpired ? "text-red-400" : isDark ? "text-gray-500" : "text-gray-400"}`}
                                />
                                <span className="font-medium">Expires:</span>
                                <span
                                  className={
                                    isExpired
                                      ? "text-red-400 font-medium"
                                      : isDark
                                        ? "text-gray-400"
                                        : "text-gray-600"
                                  }
                                >
                                  {formatDate(coupon.redeem_by)}
                                </span>
                              </div>
                            )}

                            {/* Member Count Restrictions */}
                            {(coupon.min_member_count ||
                              coupon.max_member_count) && (
                              <div
                                className={`flex items-center gap-2 p-2 rounded-lg ${isDark ? "bg-gray-900/50" : "bg-gray-50"}`}
                              >
                                <Users
                                  className={`h-4 w-4 ${isDark ? "text-gray-500" : "text-gray-400"}`}
                                />
                                <span className="font-medium">Members:</span>
                                <span
                                  className={
                                    isDark ? "text-gray-400" : "text-gray-600"
                                  }
                                >
                                  {coupon.min_member_count &&
                                  coupon.max_member_count
                                    ? `${coupon.min_member_count} - ${coupon.max_member_count}`
                                    : coupon.min_member_count
                                      ? `Min: ${coupon.min_member_count}+`
                                      : `Max: ${coupon.max_member_count}`}
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Create Coupon Modal */}
              {showCreateModal && (
                <div
                  className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
                  onClick={(e) => {
                    if (e.target === e.currentTarget) {
                      setShowCreateModal(false);
                      resetForm();
                    }
                  }}
                >
                  <div
                    className={`w-full max-w-3xl max-h-[90vh] overflow-y-auto rounded-2xl shadow-2xl ${
                      isDark
                        ? "bg-gray-800 border border-gray-700"
                        : "bg-white border border-gray-200"
                    }`}
                  >
                    {/* Modal Header */}
                    <div
                      className={`sticky top-0 z-10 flex items-center justify-between p-6 border-b ${
                        isDark
                          ? "border-gray-700 bg-gray-800"
                          : "border-gray-200 bg-white"
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div
                          className={`p-2 rounded-lg ${isDark ? "bg-blue-500/20" : "bg-blue-100"}`}
                        >
                          <Sparkles
                            className={`h-5 w-5 ${isDark ? "text-blue-400" : "text-blue-600"}`}
                          />
                        </div>
                        <div>
                          <h2
                            className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}
                          >
                            Create New Coupon
                          </h2>
                          <p
                            className={`text-sm mt-1 ${isDark ? "text-gray-400" : "text-gray-600"}`}
                          >
                            Create a discount coupon for your customers
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={() => {
                          setShowCreateModal(false);
                          resetForm();
                        }}
                        className={`p-2 rounded-lg transition-colors ${
                          isDark
                            ? "hover:bg-gray-700 text-gray-400 hover:text-white"
                            : "hover:bg-gray-100 text-gray-500 hover:text-gray-700"
                        }`}
                      >
                        <X className="h-5 w-5" />
                      </button>
                    </div>

                    {/* Modal Body */}
                    <ConfigProvider
                      theme={{
                        algorithm: isDark
                          ? antdTheme.darkAlgorithm
                          : antdTheme.defaultAlgorithm,
                        token: {
                          colorPrimary: "#3b82f6",
                          borderRadius: 12,
                        },
                      }}
                    >
                      <form
                        onSubmit={handleCreateCoupon}
                        className="p-6 space-y-6"
                      >
                        {/* Promotion Code */}
                        <div>
                          <label
                            className={`block text-sm font-semibold mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}
                          >
                            Promotion Code{" "}
                            <span className="text-gray-500 font-normal">
                              (optional)
                            </span>
                          </label>
                          <input
                            type="text"
                            value={formData.id}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                id: e.target.value.toUpperCase(),
                              })
                            }
                            className={`w-full px-4 py-3 rounded-xl border transition-colors ${
                              isDark
                                ? "bg-gray-700/50 border-gray-600 text-white focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
                                : "bg-gray-50 border-gray-300 text-gray-900 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
                            }`}
                            placeholder="TEAM35"
                          />
                          <p
                            className={`text-xs mt-2 ${isDark ? "text-gray-500" : "text-gray-500"}`}
                          >
                            Users will enter this code (e.g., TEAM35). Leave
                            empty for auto-generated code.
                          </p>
                        </div>

                        {/* Coupon Name */}
                        <div>
                          <label
                            className={`block text-sm font-semibold mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}
                          >
                            Coupon Name <span className="text-red-400">*</span>
                          </label>
                          <input
                            type="text"
                            required
                            value={formData.name}
                            onChange={(e) =>
                              setFormData({ ...formData, name: e.target.value })
                            }
                            className={`w-full px-4 py-3 rounded-xl border transition-colors ${
                              isDark
                                ? "bg-gray-700/50 border-gray-600 text-white focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
                                : "bg-gray-50 border-gray-300 text-gray-900 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
                            }`}
                            placeholder="Summer Sale 2026"
                          />
                        </div>

                        {/* Discount Type */}
                        <div>
                          <label
                            className={`block text-sm font-semibold mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}
                          >
                            Discount Type{" "}
                            <span className="text-red-400">*</span>
                          </label>
                          <div className="grid grid-cols-2 gap-3">
                            <button
                              type="button"
                              onClick={() =>
                                setFormData({
                                  ...formData,
                                  discountType: "percent",
                                })
                              }
                              className={`p-4 rounded-xl border-2 transition-all ${
                                formData.discountType === "percent"
                                  ? isDark
                                    ? "border-blue-500 bg-blue-500/20"
                                    : "border-blue-500 bg-blue-50"
                                  : isDark
                                    ? "border-gray-600 bg-gray-700/50 hover:border-gray-500"
                                    : "border-gray-300 bg-white hover:border-gray-400"
                              }`}
                            >
                              <Percent
                                className={`h-5 w-5 mb-2 mx-auto ${formData.discountType === "percent" ? "text-blue-500" : isDark ? "text-gray-400" : "text-gray-500"}`}
                              />
                              <div
                                className={`text-sm font-medium ${formData.discountType === "percent" ? "text-blue-500" : isDark ? "text-gray-400" : "text-gray-600"}`}
                              >
                                Percentage
                              </div>
                            </button>
                            {/* <button
                      type="button"
                      onClick={() => setFormData({ ...formData, discountType: 'amount' })}
                      className={`p-4 rounded-xl border-2 transition-all ${
                        formData.discountType === 'amount'
                          ? isDark 
                            ? 'border-blue-500 bg-blue-500/20' 
                            : 'border-blue-500 bg-blue-50'
                          : isDark
                            ? 'border-gray-600 bg-gray-700/50 hover:border-gray-500'
                            : 'border-gray-300 bg-white hover:border-gray-400'
                      }`}
                    >
                      <DollarSign className={`h-5 w-5 mb-2 mx-auto ${formData.discountType === 'amount' ? 'text-blue-500' : isDark ? 'text-gray-400' : 'text-gray-500'}`} />
                      <div className={`text-sm font-medium ${formData.discountType === 'amount' ? 'text-blue-500' : isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                        Fixed Amount
                      </div>
                    </button> */}
                          </div>
                        </div>

                        {/* Discount Value */}
                        {formData.discountType === "percent" ? (
                          <div>
                            <label
                              className={`block text-sm font-semibold mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}
                            >
                              Percentage Off{" "}
                              <span className="text-red-400">*</span>
                            </label>
                            <InputNumber
                              required
                              min={0.01}
                              max={100}
                              step={0.01}
                              precision={2}
                              value={
                                formData.percent_off
                                  ? parseFloat(formData.percent_off)
                                  : null
                              }
                              onChange={(value) =>
                                setFormData({
                                  ...formData,
                                  percent_off:
                                    value !== null ? value.toString() : "",
                                })
                              }
                              placeholder="34.56"
                              size="large"
                              className="w-full"
                              style={{ width: "100%" }}
                              addonAfter="%"
                            />
                            <p
                              className={`text-xs mt-2 ${isDark ? "text-gray-500" : "text-gray-500"}`}
                            >
                              Enter percentage with decimals (e.g., 34.56 for
                              34.56% off)
                            </p>
                          </div>
                        ) : (
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <label
                                className={`block text-sm font-semibold mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}
                              >
                                Amount Off{" "}
                                <span className="text-red-400">*</span>
                              </label>
                              <InputNumber
                                required
                                min={0.01}
                                step={0.01}
                                precision={2}
                                value={
                                  formData.amount_off
                                    ? parseFloat(formData.amount_off)
                                    : null
                                }
                                onChange={(value) =>
                                  setFormData({
                                    ...formData,
                                    amount_off:
                                      value !== null ? value.toString() : "",
                                  })
                                }
                                placeholder="10.50"
                                size="large"
                                className="w-full"
                                style={{ width: "100%" }}
                                addonBefore="$"
                                formatter={(value) => (value ? `${value}` : "")}
                              />
                            </div>
                            <div>
                              <label
                                className={`block text-sm font-semibold mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}
                              >
                                Currency <span className="text-red-400">*</span>
                              </label>
                              <Select
                                value={formData.currency}
                                onChange={(value) =>
                                  setFormData({ ...formData, currency: value })
                                }
                                size="large"
                                className="w-full"
                                options={[
                                  { label: "USD ($)", value: "usd" },
                                  { label: "INR (₹)", value: "inr" },
                                ]}
                              />
                            </div>
                          </div>
                        )}

                        {/* Duration */}
                        <div>
                          <label
                            className={`block text-sm font-semibold mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}
                          >
                            Duration <span className="text-red-400">*</span>
                          </label>
                          <Select
                            value={formData.duration}
                            onChange={(value) =>
                              setFormData({ ...formData, duration: value })
                            }
                            size="large"
                            className="w-full"
                            options={[
                              { label: "Once", value: "once" },
                              { label: "Repeating", value: "repeating" },
                              { label: "Forever", value: "forever" },
                            ]}
                          />
                        </div>

                        {/* Duration in Months */}
                        {formData.duration === "repeating" && (
                          <div>
                            <label
                              className={`block text-sm font-semibold mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}
                            >
                              Duration in Months{" "}
                              <span className="text-red-400">*</span>
                            </label>
                            <InputNumber
                              required
                              min={1}
                              value={
                                formData.duration_in_months
                                  ? parseInt(formData.duration_in_months)
                                  : null
                              }
                              onChange={(value) =>
                                setFormData({
                                  ...formData,
                                  duration_in_months: value
                                    ? value.toString()
                                    : "",
                                })
                              }
                              placeholder="3"
                              size="large"
                              className="w-full"
                              style={{ width: "100%" }}
                            />
                          </div>
                        )}

                        {/* Max Redemptions */}
                        <div>
                          <label
                            className={`block text-sm font-semibold mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}
                          >
                            Max Redemptions{" "}
                            <span className="text-gray-500 font-normal">
                              (optional)
                            </span>
                          </label>
                          <InputNumber
                            min={1}
                            value={
                              formData.max_redemptions
                                ? parseInt(formData.max_redemptions)
                                : null
                            }
                            onChange={(value) =>
                              setFormData({
                                ...formData,
                                max_redemptions: value ? value.toString() : "",
                              })
                            }
                            placeholder="100"
                            size="large"
                            className="w-full"
                            style={{ width: "100%" }}
                          />
                        </div>

                        {/* Expiry Date */}
                        <div>
                          <label
                            className={`block text-sm font-semibold mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}
                          >
                            Expiry Date{" "}
                            <span className="text-gray-500 font-normal">
                              (optional)
                            </span>
                          </label>
                          <DatePicker
                            showTime
                            value={
                              formData.redeem_by
                                ? dayjs(formData.redeem_by)
                                : null
                            }
                            onChange={(date) =>
                              setFormData({
                                ...formData,
                                redeem_by: date
                                  ? date.format("YYYY-MM-DDTHH:mm")
                                  : "",
                              })
                            }
                            format="YYYY-MM-DD HH:mm"
                            placeholder="Select date and time"
                            size="large"
                            className="w-full"
                            style={{ width: "100%" }}
                          />
                        </div>

                        {/* Member Count Restrictions */}
                        <div
                          className={`border-t pt-6 ${isDark ? "border-gray-700" : "border-gray-200"}`}
                        >
                          <div className="flex items-center gap-2 mb-4">
                            <Users
                              className={`h-5 w-5 ${isDark ? "text-blue-400" : "text-blue-600"}`}
                            />
                            <h3
                              className={`text-lg font-semibold ${isDark ? "text-white" : "text-gray-900"}`}
                            >
                              Member Count Restrictions
                            </h3>
                            <span
                              className={`text-xs px-2 py-0.5 rounded ${isDark ? "bg-gray-700 text-gray-400" : "bg-gray-100 text-gray-600"}`}
                            >
                              Optional
                            </span>
                          </div>
                          <p
                            className={`text-sm mb-4 ${isDark ? "text-gray-400" : "text-gray-600"}`}
                          >
                            Set minimum and/or maximum member count required to
                            use this coupon
                          </p>
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <label
                                className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}
                              >
                                Minimum Members
                              </label>
                              <InputNumber
                                min={1}
                                value={
                                  formData.min_member_count
                                    ? parseInt(formData.min_member_count)
                                    : null
                                }
                                onChange={(value) =>
                                  setFormData({
                                    ...formData,
                                    min_member_count: value
                                      ? value.toString()
                                      : "",
                                  })
                                }
                                placeholder="5"
                                size="large"
                                className="w-full"
                                style={{ width: "100%" }}
                              />
                            </div>
                            <div>
                              <label
                                className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}
                              >
                                Maximum Members
                              </label>
                              <InputNumber
                                min={1}
                                value={
                                  formData.max_member_count
                                    ? parseInt(formData.max_member_count)
                                    : null
                                }
                                onChange={(value) =>
                                  setFormData({
                                    ...formData,
                                    max_member_count: value
                                      ? value.toString()
                                      : "",
                                  })
                                }
                                placeholder="50"
                                size="large"
                                className="w-full"
                                style={{ width: "100%" }}
                              />
                            </div>
                          </div>
                        </div>

                        {/* Modal Footer */}
                        <div
                          className={`flex justify-end gap-3 pt-6 border-t ${isDark ? "border-gray-700" : "border-gray-200"}`}
                        >
                          <button
                            type="button"
                            onClick={() => {
                              setShowCreateModal(false);
                              resetForm();
                            }}
                            className={`px-6 py-3 rounded-xl font-medium transition-colors ${
                              isDark
                                ? "border border-gray-600 text-gray-300 hover:bg-gray-700"
                                : "border border-gray-300 text-gray-700 hover:bg-gray-50"
                            }`}
                          >
                            Cancel
                          </button>
                          <button
                            type="submit"
                            className="px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 !text-white rounded-xl font-medium shadow-lg hover:shadow-xl transition-all duration-200"
                          >
                            Create Coupon
                          </button>
                        </div>
                      </form>
                    </ConfigProvider>
                  </div>
                </div>
              )}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default CouponManagement;
